<template>
  <component v-if="ppcData?.ppcContent"
    :is="templateComponent"
    :ppcData="ppcData.ppcContent"
    :reviews="reviews"
    :errors="errors"
    :isFormSubmitProcessing="isFormSubmitProcessing"
    @submit="formSubmit"
  >
  </component>
  <FooterPpc26 v-if="templateKey === 'default26'" />
  <Footer v-else />
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from 'vuex/types/index.js';
import type State from '~/domains/store/State';
import { useReCaptcha } from "vue-recaptcha-v3";
import Swal from 'sweetalert2';
import { getPhoneParts, isPhoneNumberValid } from '~/utils/phone';
import { PPCTemplatesDefault, PPCTemplatesDefaultTemplate26 as PPCTemplateDefault26 } from '#components';

definePageMeta({ layout: 'ppc' });

const templateMap: Record<string, any> = {
  default: PPCTemplatesDefault,
  default26: PPCTemplateDefault26,
};

const templateComponent = computed(() => {
  return templateMap[templateKey.value] ?? PPCTemplatesDefault;
});

const nuxtApp = useNuxtApp();
const { $sg, $metadata, $leads, $ndc, $userContext, $config, $st } = nuxtApp;
const store: Store<State> = useStore();
const route = useRoute();
const router = useRouter();
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const reviews = ref<any[]>([]);
const errors = ref<string[]>([]);
const isFormSubmitProcessing = ref(false);
const templateKey = computed(() => {
  const cmsTemplate = ppcData.value?.ppcContent?.template_name as string | undefined;
  const queryTemplate = route.query.template as string | undefined;
  return (cmsTemplate || queryTemplate || 'default').trim().toLowerCase();
});

// Fetch PPC data with SSR support
const { data: ppcData } = await useAsyncData('ppc-landing-page', async () => {
  const slug = route.params.slug as string;

  const franchiseCookie = $sg.getUserFranchiseDataCookie(nuxtApp.$cookiz, "");

  let branchCode = null;
  if (route.query.utm_content) {
    if (Array.isArray(route.query.utm_content)) {
      if (route.query.utm_content[0] ?? false) {
        branchCode = route.query.utm_content[0];
      }
    } else {
      branchCode = route.query.utm_content;
    }
  } else {
    branchCode = franchiseCookie?.branchCode;
  }

  const [ppcDataResponse, orgStructuredData, aggReviewData] = await Promise.all([
    $sg.getOctoberData(`ppc/${slug}`),
    $metadata.getOrganizationStructuredData(),
    $metadata.getAggregateRatingStructuredData(branchCode || ''),
  ]);

  if (!ppcDataResponse || ppcDataResponse.length === 0) {
    await navigateTo('/');
    return null;
  }

  const ppcDataObj = ppcDataResponse[0];

  if (ppcDataObj.redirect_url) {
    await navigateTo(`/ppc/${ppcDataObj.redirect_url}`);
    return null;
  }

  return {
    ppcContent: ppcDataObj,
    orgData: orgStructuredData,
    aggReviewData,
    branchCode,
  };
});

// Setup SEO with SSR support
if (ppcData.value) {
  const runtimeConfig = useRuntimeConfig();
  const frontEndUrl = (runtimeConfig.public?.frontEndUrl || 'https://www.spring-green.com').replace(/\/$/, '');
  const canonicalUrl = `${frontEndUrl}${route.path}${route.path.endsWith('/') ? '' : '/'}`;
  
  const ogTitle = ppcData.value.ppcContent.social_title || ppcData.value.ppcContent.seo_title || ppcData.value.ppcContent.title;
  const ogDescription = ppcData.value.ppcContent.social_description || ppcData.value.ppcContent.meta_description || ppcData.value.ppcContent.excerpt;
  const ogImage = ppcData.value.ppcContent.social_image ? `${$config.public.sgCmsImage}${ppcData.value.ppcContent.social_image}` : undefined;

  useSeoHead({
    title: ppcData.value.ppcContent.seo_title || ppcData.value.ppcContent.name,
    description: ppcData.value.ppcContent.meta_description || ppcData.value.ppcContent.excerpt,
    robotsIndex: ppcData.value.ppcContent.meta_robots_index,
    robotsFollow: ppcData.value.ppcContent.meta_robots_follow,
    openGraph: {
      title: ogTitle,
      description: ogDescription,
      image: ogImage,
      url: canonicalUrl,
      type: 'website',
    },
    twitter: {
      title: ogTitle,
      description: ogDescription,
      image: ogImage,
      card: 'summary_large_image',
    },
    orgData: ppcData.value.orgData,
    aggReviewData: ppcData.value.aggReviewData,
  });
}

const recaptcha = async () => {
  await recaptchaLoaded(); 
  return await executeRecaptcha("quote");
};

const validEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

const formSubmit = async (formData: any) => {
  if (!ppcData.value) {
    console.error("PPC data not loaded");
    return;
  }

  try {
    isFormSubmitProcessing.value = true;
    store.commit("SET_SHOW_LOADER", true);

    errors.value = [];

    if (!formData.firstName) {
      errors.value.push("First Name Required");
    }

    if (!formData.lastName) {
      errors.value.push("Last Name Required");
    }

    if (!formData.emailAddress) {
      errors.value.push("Email required.");
    } else if (!validEmail(formData.emailAddress)) {
      errors.value.push("Valid email required.");
    }

    if (!isPhoneNumberValid(formData.pnumber)) {
      errors.value.push("Phone number required");
    }

    if (!formData.address) {
      errors.value.push("Street address required.");
    }

    if (!formData.city) {
      errors.value.push("City required.");
    }

    if (!formData.state) {
      errors.value.push("State required.");
    }

    if (!formData.zipcode) {
      errors.value.push("Zipcode required.");
    }

    const addressSuggestions = await $st.lookup(
      `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zipcode}`
    );
    if (!(addressSuggestions?.suggestions?.length > 0)) {
      errors.value.push(
        "Unable to find this address - valid address required."
      );
    }

    if (errors.value.length > 0) {
      isFormSubmitProcessing.value = false;
      store.commit("SET_SHOW_LOADER", false);
      return;
    }

    const phoneParts = getPhoneParts(formData.pnumber);
    formData.areaCode = phoneParts?.areaCode ?? "";
    formData.phonePrefix = phoneParts?.phonePrefix ?? "";
    formData.phoneNumber = phoneParts?.phoneNumber ?? "";

    const branchCode = store.state.persistedState.branchCode ||
    store.state.persistedState.branchInfo?.BranchCode ||
    "";

    const dmSource = ppcData.value.ppcContent.ppc_dmsource
      ? ppcData.value.ppcContent.ppc_dmsource
      : route.query.utm_source ?? "";
    const dmContent = branchCode;

    const data = {
      ...formData,
      createRecord: true,
      dmSource: dmSource,
      dmContent: dmContent,
      contactType: "PPC_LANDING_CONTACT",
      ...$metadata.getPageMetadataObj(),
      serviceType: ppcData.value.ppcContent.service_type,
    };

    // grecaptcha.ready(async () => {
      try {
        // const token = await grecaptcha.execute($config.public.recaptchaSitekey, {action: "submit",});
        const token = await recaptcha(); 
        if (!token) {
          isFormSubmitProcessing.value = false;
          store.commit("SET_SHOW_LOADER", false);
          Swal.fire({
            title: "Invalid reCAPTCHA.\nPlease try again.",
            icon: "warning",
            customClass: {
              popup: "swal2-border-radius",
              confirmButton: "swal2-border-radius",
            },
          });
          return; 
        }

        const form = await $leads.submitPpcContactForm(data);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "ppc_marketing_formSubmission",
          formID: "Personalized_Quote",
        });

        isFormSubmitProcessing.value = false;
        store.commit("SET_SHOW_LOADER", false);
        Swal.fire({
          title: "Form submitted successfully.",
          customClass: {
            popup: "swal2-border-radius",
            confirmButton: "swal2-border-radius",
          },
        });

        let redirectUrl = "";
        if (
          ppcData.value.ppcContent.thank_you_url?.length !== 0 &&
          ppcData.value.ppcContent.redirect_franchise_lookup === "0"
        ) {
          redirectUrl = ppcData.value.ppcContent.thank_you_url;
        }

        $sg.addressLookup(
          `${formData.address}, ${formData.city}, ${formData.state}, ${formData.zipcode}`,
          nuxtApp,
          true,
          redirectUrl,
          undefined,
          undefined,
          true
        );
      } catch (err) {
        console.error(err);
        isFormSubmitProcessing.value = false;
        store.commit("SET_SHOW_LOADER", false);
        Swal.fire({
          title:
            "There was an error processing your request, or you may be outside of our service area.\nPlease try again later.",
          customClass: {
            popup: "swal2-border-radius",
            confirmButton: "swal2-border-radius",
          },
        });
      }
  //   });
  } catch (err) {
    console.error(err);
    isFormSubmitProcessing.value = false;
    store.commit("SET_SHOW_LOADER", false);
    Swal.fire({
      title: "Something went wrong. Please try again later.",
      customClass: {
        popup: "swal2-border-radius",
        confirmButton: "swal2-border-radius",
      },
    });
  }
};

// Handle branch code updates and IMC postcard URL fix on client side
onMounted(async () => {
  const branchCode = route.query.utm_content;

  if (branchCode && branchCode !== store.state.persistedState.branchCode) {
    await $userContext.metaRefresh({
      clearState: true,
      stateUpdate: {
        branchCode: route.query.utm_content,
      },
      refresh: new Set(["ALL"]),
    });
  } else {
    await $userContext.metaRefresh();
  }

  /* IMC broken postcard URL fix */
  if (ppcData?.value?.ppcContent?.package_id && !!route.query.franchise_id && !route.query.utm_medium) {
    const franchiseId = route.query.franchise_id;
    const customerId = route.query.c;
    const ndcInfo = store.state.persistedState.ndcInfo;

    await $ndc.addTouchPoint(ndcInfo.ndc, true);

    // Redirect to the correct URL
    router.push(`/ppc-landing-page/premium-package?franchise_id=${franchiseId}&c=${customerId}&utm_content=${franchiseId}&ndc=${ndcInfo.ndc}&utm_source=IMC&utm_medium=postcard&isQR=true`);
  }
});
</script>

<style scoped>
</style>
